import React, { useState } from 'react';
import { Calendar, Copy, Check, MapPinned } from 'lucide-react';
import {
  FEED_CARD_SHELL,
  FEED_TITLE,
} from './investigation/feedCardStyles';
import MatchBadge from './investigation/MatchBadge';

function resolveGhuntHasMaps(data, details, gaia) {
  if (details.has_maps === true) return true;
  if (gaia) return true;
  if (data.maps_url || details.maps_url) return true;
  if (details.active_services_urls?.Maps) return true;
  const mapsInspect = details.maps_inspect;
  if (mapsInspect?.gaia_id) return true;
  if (Array.isArray(mapsInspect?.recent_reviews) && mapsInspect.recent_reviews.length > 0) return true;
  if (mapsInspect?.stats && typeof mapsInspect.stats === 'object' && Object.keys(mapsInspect.stats).length > 0) {
    return true;
  }
  return false;
}

function resolveGhuntHasCalendar(data, details) {
  if (details.has_calendar === true) return true;
  if (details.calendar_inspect?.has_public_calendar) return true;
  if (details.calendar_url || data.calendar_url) return true;
  return false;
}

/**
 * Verified Google Identity card (GHunt footprint) with in-app Maps / Calendar Inspect.
 *
 * @param {{ data: object, onInspectMaps?: Function, onInspectCalendar?: Function }} props
 */
export default function GHuntIdentityCard({ data, onInspectMaps, onInspectCalendar }) {
  const [copied, setCopied] = useState(false);

  if (!data) return null;

  const details = data.details && typeof data.details === 'object' ? data.details : {};
  const gaia = String(data.gaia_id || details.gaia_id || '').trim();
  const displayName = details.name || data.title || 'Verified Google Identity';
  const photo =
    data.profile_photo_url || details.profile_photo_url || details.photo_url;
  const hasMaps = resolveGhuntHasMaps(data, details, gaia);
  const hasCalendar = resolveGhuntHasCalendar(data, details);

  const copyGaiaId = async () => {
    if (!gaia || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(gaia);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article
      className={`${FEED_CARD_SHELL} border-blue-500/30`}
      data-testid="ghunt-identity-card"
    >
      <section className="flex flex-col gap-4">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-blue-500/30 bg-blue-950/40 text-2xl shadow-inner">
              {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : 'G'}
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-300">
                Verified Google Identity
              </h4>
              <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
                <p className={`truncate ${FEED_TITLE}`}>{displayName}</p>
                <span className="inline-flex items-center rounded-md border border-emerald-500/40 bg-emerald-950/50 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-300">
                  Verified
                </span>
                <MatchBadge value={data.score ?? 95} />
              </div>
              {gaia ? (
                <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2">
                  <p className="font-mono text-[11px] text-slate-400">
                    Gaia ID: <span className="text-slate-200">{gaia}</span>
                  </p>
                  <button
                    type="button"
                    onClick={copyGaiaId}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-600/70 bg-slate-900/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-200"
                    data-testid="ghunt-copy-gaia-id"
                    title="Copy Gaia ID"
                  >
                    {copied ? (
                      <Check className="h-3 w-3 text-emerald-400" aria-hidden />
                    ) : (
                      <Copy className="h-3 w-3" aria-hidden />
                    )}
                    {copied ? 'Copied!' : 'Copy ID'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {hasMaps || hasCalendar ? (
          <div>
            <p className="mb-2 text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">
              Active Cloud Services
            </p>
            <div className="flex flex-wrap gap-2">
              {hasMaps ? (
                <button
                  type="button"
                  onClick={() => onInspectMaps?.(data)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-200 transition hover:border-cyan-500/50 hover:text-cyan-100"
                  data-testid="ghunt-inspect-maps"
                >
                  <MapPinned className="h-3.5 w-3.5 shrink-0 text-cyan-300" aria-hidden />
                  Inspect Maps
                </button>
              ) : null}
              {hasCalendar ? (
                <button
                  type="button"
                  onClick={() => onInspectCalendar?.(data)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-200 transition hover:border-cyan-500/50 hover:text-cyan-100"
                  data-testid="ghunt-inspect-calendar"
                >
                  <Calendar className="h-3.5 w-3.5 shrink-0 text-cyan-300" aria-hidden />
                  Inspect Calendar
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
    </article>
  );
}
